/**
 * Дашборд ОУП — брифинг по портфелю: формулы на виду, drill-down, нейтральная «н/д».
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  getContractors,
  getContracts,
  getPlanfactOperations,
  getTasks,
  getWorkTypes,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatContractSumDisplay } from '../utils/table';
import { formatDeadlineCompact, ContractDeadlineInfo } from '../utils/deadline';
import { formatSignedBalance } from '../utils/taskFinance';
import {
  BriefingContractItem,
  BriefingTaskItem,
  PortfolioBriefingData,
  TaskMatrixColumn,
  TaskMatrixRow,
  TasksMatrix,
  TasksOverdueMatrix,
  buildPortfolioBriefing,
} from '../utils/portfolioBriefing';
import '../styles/DashboardOup4.css';

const MATRIX_COLUMN_LABELS: Record<TaskMatrixColumn, string> = {
  project: 'Проект',
  montage: 'Монтаж',
  startup: 'Пуск',
};

const MATRIX_ROW_LABELS: Record<TaskMatrixRow, string> = {
  fl: 'ФЛ',
  ul: 'ЮЛ',
};

type ValueTone = 'default' | 'positive' | 'negative' | 'warn' | 'muted';
type MeterTone = 'ok' | 'warn' | 'over' | 'muted';
type DonutTone = 'default' | 'success' | 'danger' | 'warn';

interface DrawerItem {
  to: string;
  title: string;
  subtitle: string;
  badge?: string;
}

interface DrawerState {
  title: string;
  items: DrawerItem[];
  footerTo: string;
  footerLabel: string;
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function formatRubExact(value: number): string {
  return `${formatContractSumDisplay(value)} ₽`;
}

function formatRubHero(value: number, signed = false): string {
  const abs = Math.abs(Math.round(value));
  let sign = '';
  if (signed) {
    if (value > 0) sign = '+';
    else if (value < 0) sign = '−';
  } else if (value < 0) {
    sign = '−';
  }

  if (abs >= 1_000_000) {
    const mln = abs / 1_000_000;
    const digits = mln >= 100 ? 0 : 1;
    const body = mln.toLocaleString('ru-RU', {
      maximumFractionDigits: digits,
      minimumFractionDigits: 0,
    });
    return `${sign}${body} млн ₽`;
  }

  if (abs >= 10_000) {
    const body = Math.round(abs / 1_000).toLocaleString('ru-RU');
    return `${sign}${body} тыс. ₽`;
  }

  return `${sign}${formatContractSumDisplay(abs)} ₽`;
}

function formatAsOfDate(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function firstNameOf(fullName: string | undefined): string | null {
  const name = fullName?.trim().split(/\s+/)[0];
  return name || null;
}

function contractHref(item: BriefingContractItem): string {
  return `/contracts/${item.id}`;
}

function taskHref(item: BriefingTaskItem): string {
  return `/tasks/${item.id}?contracts_task_id=${item.contractsTaskId}`;
}

function overdueDayCount(deadline: ContractDeadlineInfo): number {
  if (!deadline.overdue) return 0;
  return (deadline.months ?? 0) * 30 + (deadline.days ?? 0);
}

function overdueBadge(deadline: ContractDeadlineInfo): string | undefined {
  if (!deadline.overdue) return undefined;
  const compact = formatDeadlineCompact(deadline);
  return compact === '—' ? 'Просрочка' : `Просрочка ${compact}`;
}

function contractDrawerItems(items: BriefingContractItem[]): DrawerItem[] {
  return [...items]
    .sort((a, b) => overdueDayCount(b.deadline) - overdueDayCount(a.deadline))
    .map((item) => {
      const address = item.address?.trim();
      const contractor = item.contractorName?.trim();
      const subtitle = [contractor, address].filter(Boolean).join(' · ') || 'Адрес не указан';
      return {
        to: contractHref(item),
        title: item.contractNumber || `Договор ${item.id}`,
        subtitle,
        badge: overdueBadge(item.deadline),
      };
    });
}

function taskDrawerItems(items: BriefingTaskItem[]): DrawerItem[] {
  return [...items]
    .sort((a, b) => overdueDayCount(b.deadline) - overdueDayCount(a.deadline))
    .map((item) => ({
      to: taskHref(item),
      title: item.taskId || `Задача ${item.id}`,
      subtitle: [item.shortName, item.contractNumber].filter(Boolean).join(' · ') || 'Без названия',
      badge: overdueBadge(item.deadline),
    }));
}

function budgetMeterTone(percent: number | null, overrun: boolean): MeterTone {
  if (percent == null) return 'muted';
  if (overrun || percent >= 100) return 'over';
  if (percent >= 85) return 'warn';
  return 'ok';
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path
        d="M6.2 3.2 11 8l-4.8 4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoButton({ text }: { text: string }) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);

  return (
    <span className="oup4-info">
      <button
        type="button"
        className="oup4-info__btn"
        aria-label="Пояснение"
        aria-describedby={tooltipId}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onBlur={() => setOpen(false)}
      >
        i
      </button>
      <span id={tooltipId} role="tooltip" className={`oup4-info__tooltip${open ? ' is-open' : ''}`}>
        {text}
      </span>
    </span>
  );
}

function LabeledDonut({
  percent,
  tone,
  caption,
}: {
  percent: number;
  tone: DonutTone;
  caption: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const r = 18;
  const c = 2 * Math.PI * r;
  const offset = c - (clamped / 100) * c;

  return (
    <div
      className={`oup4-donut oup4-donut--${tone}`}
      role="img"
      aria-label={`${caption}: ${clamped}%`}
    >
      <svg viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r={r} fill="none" stroke="var(--portal-border-finance)" strokeWidth="4" />
        <circle
          className="oup4-donut__progress"
          cx="22"
          cy="22"
          r={r}
          fill="none"
          strokeWidth="4"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span className="oup4-donut__label" aria-hidden="true">
        {clamped}%
      </span>
    </div>
  );
}

function Meter({
  percent,
  tone,
  caption,
  hideCaption = false,
}: {
  percent: number;
  tone: MeterTone;
  caption: string;
  hideCaption?: boolean;
}) {
  const clamped = Math.max(0, Math.round(percent));
  const width = Math.min(100, clamped);

  return (
    <div className="oup4-meter">
      <div className="oup4-meter__bar" aria-hidden="true">
        <div className={`oup4-meter__fill oup4-meter__fill--${tone}`} style={{ width: `${width}%` }} />
      </div>
      {hideCaption ? null : (
        <div className="oup4-meter__caption">
          {clamped}% {caption}
        </div>
      )}
    </div>
  );
}

function FormulaTable({
  rows,
  result,
  resultTone = 'default',
}: {
  rows: Array<{ op?: string; value: string; label: string }>;
  result: { value: string; label: string };
  resultTone?: 'default' | 'positive' | 'negative';
}) {
  const resultClass =
    resultTone === 'positive'
      ? ' oup4-ledger__value--positive'
      : resultTone === 'negative'
        ? ' oup4-ledger__value--negative'
        : '';

  return (
    <div className="oup4-ledger" role="table">
      {rows.map((row) => (
        <div className="oup4-ledger__row" role="row" key={row.label}>
          <span className="oup4-ledger__op" role="cell">
            {row.op ?? ''}
          </span>
          <span className="oup4-ledger__value" role="cell">
            {row.value}
          </span>
          <span className="oup4-ledger__label" role="cell">
            {row.label}
          </span>
        </div>
      ))}
      <div className="oup4-ledger__row oup4-ledger__row--result" role="row">
        <span className="oup4-ledger__op" role="cell">
          =
        </span>
        <span className={`oup4-ledger__value${resultClass}`} role="cell">
          {result.value}
        </span>
        <span className="oup4-ledger__label" role="cell">
          {result.label}
        </span>
      </div>
    </div>
  );
}

function valueClass(tone: ValueTone): string {
  if (tone === 'positive') return ' oup4-metric__value--positive';
  if (tone === 'negative') return ' oup4-metric__value--negative';
  if (tone === 'warn') return ' oup4-metric__value--warn';
  if (tone === 'muted') return ' oup4-metric__value--muted';
  return '';
}

function MetricValue({
  children,
  tone = 'default',
  onOpen,
  openLabel,
}: {
  children: React.ReactNode;
  tone?: ValueTone;
  onOpen?: () => void;
  openLabel?: string;
}) {
  const className = `oup4-metric__value${valueClass(tone)}`;
  if (!onOpen) {
    return <div className={className}>{children}</div>;
  }

  return (
    <button type="button" className={`oup4-metric-btn ${className}`} onClick={onOpen} aria-label={openLabel}>
      <span className="oup4-metric-btn__text">{children}</span>
      <Chevron className="oup4-metric-btn__chevron" />
    </button>
  );
}

function TasksMatrixTable({
  matrix,
  onCell,
}: {
  matrix: TasksMatrix;
  onCell: (row: TaskMatrixRow, column: TaskMatrixColumn) => void;
}) {
  const rows = matrix.rows.filter((row) => matrix.columns.some((column) => matrix.counts[row][column] > 0));
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="oup4-tiles" role="table">
      <div className="oup4-tiles__row oup4-tiles__row--head" role="row">
        <span className="oup4-tiles__corner" role="columnheader" />
        {matrix.columns.map((column) => (
          <span key={column} className="oup4-tiles__head" role="columnheader">
            {MATRIX_COLUMN_LABELS[column]}
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="oup4-tiles__row" role="row" key={row}>
          <span className="oup4-tiles__row-label" role="rowheader">
            {MATRIX_ROW_LABELS[row]}
          </span>
          {matrix.columns.map((column) => {
            const count = matrix.counts[row][column];
            return (
              <span className="oup4-tiles__cell" role="cell" key={column}>
                {count > 0 ? (
                  <button
                    type="button"
                    className="oup4-tiles__btn"
                    onClick={() => onCell(row, column)}
                    aria-label={`${MATRIX_ROW_LABELS[row]}, ${MATRIX_COLUMN_LABELS[column]}: ${count}`}
                  >
                    {count}
                  </button>
                ) : (
                  <span className="oup4-tiles__empty">—</span>
                )}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function OverdueTasksMatrixTable({
  matrix,
  onCell,
}: {
  matrix: TasksOverdueMatrix;
  onCell: (row: TaskMatrixRow, column: TaskMatrixColumn) => void;
}) {
  const rows = matrix.rows.filter((row) => matrix.columns.some((column) => matrix.cells[row][column].total > 0));
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="oup4-tiles" role="table">
      <div className="oup4-tiles__row oup4-tiles__row--head" role="row">
        <span className="oup4-tiles__corner" role="columnheader" />
        {matrix.columns.map((column) => (
          <span key={column} className="oup4-tiles__head" role="columnheader">
            {MATRIX_COLUMN_LABELS[column]}
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="oup4-tiles__row" role="row" key={row}>
          <span className="oup4-tiles__row-label" role="rowheader">
            {MATRIX_ROW_LABELS[row]}
          </span>
          {matrix.columns.map((column) => {
            const cell = matrix.cells[row][column];
            if (cell.total <= 0) {
              return (
                <span className="oup4-tiles__cell" role="cell" key={column}>
                  <span className="oup4-tiles__empty">—</span>
                </span>
              );
            }
            const label = `${MATRIX_ROW_LABELS[row]}, ${MATRIX_COLUMN_LABELS[column]}: ${cell.overdue} из ${cell.total} просрочено`;
            const body = (
              <>
                <strong>{cell.overdue}</strong>
                <span className="oup4-tiles__of"> из {cell.total}</span>
              </>
            );
            if (cell.overdue <= 0) {
              return (
                <span className="oup4-tiles__cell" role="cell" key={column}>
                  <span className="oup4-tiles__static">{body}</span>
                </span>
              );
            }
            return (
              <span className="oup4-tiles__cell" role="cell" key={column}>
                <button
                  type="button"
                  className="oup4-tiles__btn oup4-tiles__btn--warn"
                  onClick={() => onCell(row, column)}
                  aria-label={label}
                >
                  {body}
                </button>
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function DrillDownDrawer({
  drawer,
  onClose,
}: {
  drawer: DrawerState;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="oup4-drawer" role="dialog" aria-modal="true" aria-labelledby="oup4-drawer-title">
      <button type="button" className="oup4-drawer__backdrop" aria-label="Закрыть список" onClick={onClose} />
      <aside className="oup4-drawer__panel">
        <div className="oup4-drawer__head">
          <div>
            <h2 id="oup4-drawer-title" className="oup4-drawer__title">
              {drawer.title}
            </h2>
            <p className="oup4-drawer__count">
              {drawer.items.length} {pluralRu(drawer.items.length, 'запись', 'записи', 'записей')}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="oup4-drawer__close"
            aria-label="Закрыть"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {drawer.items.length === 0 ? (
          <p className="oup4-drawer__empty">Список пуст.</p>
        ) : (
          <ul className="oup4-drawer__list">
            {drawer.items.map((item) => (
              <li key={`${item.to}-${item.title}`}>
                <Link className="oup4-drawer__item" to={item.to} onClick={onClose}>
                  <div className="oup4-drawer__item-body">
                    <div className="oup4-drawer__item-title">
                      <span>{item.title}</span>
                      {item.badge ? <span className="oup4-drawer__badge">{item.badge}</span> : null}
                    </div>
                    <div className="oup4-drawer__item-sub">{item.subtitle}</div>
                  </div>
                  <Chevron className="oup4-drawer__chevron" />
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className="oup4-drawer__footer">
          <Link className="oup4-drawer__footer-link" to={drawer.footerTo} onClick={onClose}>
            {drawer.footerLabel}
            <Chevron className="oup4-drawer__footer-chevron" />
          </Link>
        </div>
      </aside>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="oup4-layout" aria-busy="true" aria-live="polite">
      <section className="oup4-section">
        <div className="oup4-skel oup4-skel--label" aria-hidden="true" />
        <div className="oup4-bento oup4-bento--3">
          <div className="oup4-card oup4-skel oup4-skel--card" aria-hidden="true" />
          <div className="oup4-card oup4-skel oup4-skel--card" aria-hidden="true" />
          <div className="oup4-card oup4-skel oup4-skel--card" aria-hidden="true" />
        </div>
      </section>
      <div className="oup4-layout__split">
        <section className="oup4-card oup4-skel oup4-skel--tall" aria-hidden="true" />
        <section className="oup4-card oup4-skel oup4-skel--mid" aria-hidden="true" />
      </div>
      <span className="oup4-visually-hidden">Загрузка показателей портфеля</span>
    </div>
  );
}

const DashboardOup4Page: React.FC = () => {
  const { hasDepartment, user } = useAuth();
  const [data, setData] = useState<PortfolioBriefingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const canOpenPage = hasDepartment('OUP_UL');
  const seesEntireCompany = hasDepartment('ALL_COMP');
  const asOfLabel = useMemo(() => formatAsOfDate(new Date()), []);
  const firstName = firstNameOf(user?.full_name);

  const fetchData = useCallback(async (): Promise<void> => {
    if (!seesEntireCompany && user?.id == null) {
      setIsLoading(false);
      setData(null);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      const [contracts, tasks, planfactOperations, workTypes, contractors] = await Promise.all([
        getContracts(),
        getTasks(),
        getPlanfactOperations().catch(() => []),
        getWorkTypes().catch(() => []),
        getContractors().catch(() => []),
      ]);

      const contractorNameById = new Map(
        contractors.map((contractor) => [contractor.id, contractor.name]),
      );

      setData(
        buildPortfolioBriefing(
          contracts,
          tasks,
          planfactOperations,
          workTypes,
          {
            ...(seesEntireCompany ? {} : { projectLeadId: user?.id }),
            contractorNameById,
          },
        ),
      );
    } catch (error) {
      console.error('Ошибка при загрузке dashboard-oup4:', error);
      setLoadError('Не удалось загрузить данные портфеля.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [seesEntireCompany, user?.id]);

  useEffect(() => {
    if (!canOpenPage) {
      return;
    }
    void fetchData();
  }, [canOpenPage, fetchData]);

  const openContracts = useCallback(
    (items: BriefingContractItem[], title: string) => {
      setDrawer({
        title,
        items: contractDrawerItems(items),
        footerTo: '/in-progress/contracts',
        footerLabel: 'Все договоры в работе',
      });
    },
    [],
  );

  const openTasks = useCallback((items: BriefingTaskItem[], title: string) => {
    setDrawer({
      title,
      items: taskDrawerItems(items),
      footerTo: '/in-progress/tasks',
      footerLabel: 'Все задачи в работе',
    });
  }, []);

  if (!canOpenPage) {
    return <Navigate to="/" replace />;
  }

  const cashFlowTone: ValueTone =
    data && data.cashFlow > 0 ? 'positive' : data && data.cashFlow < 0 ? 'negative' : 'default';
  const budgetTone: ValueTone = !data || data.budgetRemainder == null
    ? 'default'
    : data.budgetOverrun
      ? 'negative'
      : data.budgetRemainder > 0
        ? 'positive'
        : 'default';
  const debtTone: ValueTone = data && data.conceptualDebt > 0 ? 'negative' : 'default';
  const closedPaid = data ? Math.max(0, data.closedTasksAmount - data.conceptualDebt) : 0;

  return (
    <div className="dashboard-oup4">
      <header className="dashboard-oup4__top">
        <h1>Это показатели твоего портфеля договоров в роли РП</h1>
        <div className="dashboard-oup4__meta">
          <span className="oup4-chip">На {asOfLabel}</span>
          {data ? (
            <span className="oup4-chip">
              {data.contractCount} {pluralRu(data.contractCount, 'договор', 'договора', 'договоров')} в работе
            </span>
          ) : null}
          {firstName ? <span className="oup4-chip">{firstName}</span> : null}
        </div>
      </header>

      {isLoading ? (
        <Skeleton />
      ) : loadError ? (
        <div className="oup4-state oup4-state--error">
          <p>{loadError}</p>
          <button type="button" className="oup4-state__btn" onClick={() => void fetchData()}>
            Повторить
          </button>
        </div>
      ) : !data || data.contractCount === 0 ? (
        <div className="oup4-state">
          {seesEntireCompany
            ? 'В портфеле пока нет договоров.'
            : 'Нет договоров, где ты руководитель проекта.'}
        </div>
      ) : (
        <div className="oup4-layout">
          <section className="oup4-section" aria-labelledby="oup4-portfolio-title">
            <h2 id="oup4-portfolio-title" className="oup4-section__title">
              Портфель
            </h2>
            <div className="oup4-bento oup4-bento--3">
              <article className="oup4-card">
                <div className="oup4-metric__label">Договоры</div>
                <MetricValue
                  onOpen={() => openContracts(data.contractItems, 'Договоры в портфеле')}
                  openLabel="Открыть список договоров"
                >
                  {data.contractCount}
                </MetricValue>
                <p className="oup4-metric__caption">
                  {seesEntireCompany ? 'семьи со статусом «в работе»' : 'где ты руководитель проекта'}
                </p>
              </article>

              <article className="oup4-card">
                <div className="oup4-metric__label">Сумма</div>
                <div className="oup4-metric__value">{formatRubHero(data.portfolioSum)}</div>
                <p className="oup4-metric__exact">{formatRubExact(data.portfolioSum)}</p>
                <div className="oup4-paid">
                  <div className="oup4-paid__text">
                    <div className="oup4-metric__label">Оплачено</div>
                    <div className="oup4-metric__exact">{formatRubExact(data.paymentPaid)}</div>
                  </div>
                  <LabeledDonut percent={data.paymentPercent} tone="success" caption="оплачено" />
                </div>
              </article>

              <article className="oup4-card">
                <div className="oup4-metric__label">Задачи</div>
                <MetricValue
                  onOpen={
                    data.taskCount > 0
                      ? () => openTasks(data.taskItems, 'Задачи в работе')
                      : undefined
                  }
                  openLabel="Открыть список задач"
                >
                  {data.taskCount}
                </MetricValue>
                <p className="oup4-metric__caption">в работе</p>
                <TasksMatrixTable
                  matrix={data.tasksInWorkMatrix}
                  onCell={(row, column) =>
                    openTasks(
                      data.taskItems.filter((item) => item.row === row && item.column === column),
                      `${MATRIX_ROW_LABELS[row]} · ${MATRIX_COLUMN_LABELS[column]}`,
                    )
                  }
                />
              </article>
            </div>
          </section>

          <div className="oup4-layout__split">
            <section className="oup4-section" aria-labelledby="oup4-money-title">
              <h2 id="oup4-money-title" className="oup4-section__title">
                Деньги
              </h2>
              <div className="oup4-stack">
                <article className="oup4-card oup4-card--split">
                  <div>
                    <div className="oup4-metric__label">Денежный поток по договорам</div>
                    <div className={`oup4-metric__value${valueClass(cashFlowTone)}`}>
                      {formatRubHero(data.cashFlow, true)}
                    </div>
                    <p className="oup4-metric__exact">{formatSignedBalance(data.cashFlow)} ₽</p>
                    <p className="oup4-metric__caption">поступления минус расходы</p>
                  </div>
                  <FormulaTable
                    rows={[
                      { value: formatRubExact(data.receiptsTotal), label: 'поступления' },
                      { op: '−', value: formatRubExact(data.expenseFact), label: 'расходы' },
                    ]}
                    result={{
                      value: `${formatSignedBalance(data.cashFlow)} ₽`,
                      label: 'поток',
                    }}
                    resultTone={cashFlowTone === 'positive' || cashFlowTone === 'negative' ? cashFlowTone : 'default'}
                  />
                </article>

                <article className={`oup4-card${data.expensePlan != null ? ' oup4-card--split' : ''}`}>
                  <div>
                    <div className="oup4-metric__label">Остаток бюджета по договорам</div>
                    <div className={`oup4-metric__value${valueClass(budgetTone)}`}>
                      {data.budgetRemainder != null
                        ? formatRubHero(data.budgetRemainder, true)
                        : formatRubHero(data.expenseFact)}
                    </div>
                    {data.budgetRemainder != null ? (
                      <p className="oup4-metric__exact">{formatSignedBalance(data.budgetRemainder)} ₽</p>
                    ) : (
                      <p className="oup4-metric__caption">план не задан, показан факт расходов</p>
                    )}
                  </div>
                  {data.expensePlan != null ? (
                    <div className="oup4-ledger-wrap">
                      <FormulaTable
                        rows={[
                          { value: formatRubExact(data.expensePlan), label: 'плановые расходы' },
                          { op: '−', value: formatRubExact(data.expenseFact), label: 'фактические расходы' },
                        ]}
                        result={{
                          value:
                            data.budgetRemainder != null
                              ? `${formatSignedBalance(data.budgetRemainder)} ₽`
                              : formatRubExact(data.expenseFact),
                          label: 'остаток',
                        }}
                        resultTone={
                          budgetTone === 'positive' || budgetTone === 'negative' ? budgetTone : 'default'
                        }
                      />
                      {data.budgetPercent != null ? (
                        <Meter
                          percent={data.budgetPercent}
                          tone={budgetMeterTone(data.budgetPercent, data.budgetOverrun)}
                          caption="освоено"
                        />
                      ) : null}
                    </div>
                  ) : null}
                </article>

                <article className="oup4-card oup4-card--split">
                  <div>
                    <div className="oup4-metric__label">
                      Не оплачено по закрытым задачам
                      <InfoButton text="Расходы по задаче закрыты, а оплата ещё не поступила. В учёте это понятийный долг." />
                    </div>
                    <div className={`oup4-metric__value${valueClass(debtTone)}`}>
                      {formatRubHero(data.conceptualDebt)}
                    </div>
                    <p className="oup4-metric__exact">{formatRubExact(data.conceptualDebt)}</p>
                    <p className="oup4-metric__caption">понятийный долг</p>
                  </div>
                  <div className="oup4-ledger-wrap">
                    <FormulaTable
                      rows={[
                        { value: formatRubExact(data.closedTasksAmount), label: 'закрытые задачи' },
                        { op: '−', value: formatRubExact(closedPaid), label: 'оплачено по ним' },
                      ]}
                      result={{
                        value: formatRubExact(data.conceptualDebt),
                        label: 'не оплачено',
                      }}
                      resultTone={debtTone === 'negative' ? 'negative' : 'default'}
                    />
                    {data.conceptualDebtPercent != null ? (
                      <Meter
                        percent={data.conceptualDebtPercent}
                        tone={data.conceptualDebt > 0 ? (data.conceptualDebtPercent >= 100 ? 'over' : 'warn') : 'ok'}
                        caption="не оплачено"
                      />
                    ) : null}
                  </div>
                </article>

                <article className="oup4-card oup4-card--muted">
                  <div className="oup4-metric__label">Дебиторская задолженность</div>
                  <div className="oup4-metric__value oup4-metric__value--muted">Нет данных</div>
                  <p className="oup4-metric__caption">появится после учёта актов</p>
                </article>
              </div>
            </section>

            <section className="oup4-section" aria-labelledby="oup4-deadlines-title">
              <h2 id="oup4-deadlines-title" className="oup4-section__title">
                Сроки
              </h2>
              <div className="oup4-stack">
                <article className="oup4-card">
                  <div className="oup4-metric__label">Договоры в просрочке</div>
                  <div className="oup4-metric__frac">
                    <MetricValue
                      tone={data.overdueContractCount > 0 ? 'negative' : 'default'}
                      onOpen={
                        data.overdueContractCount > 0
                          ? () =>
                              openContracts(
                                data.contractItems.filter((item) => item.overdue),
                                'Просроченные договоры',
                              )
                          : undefined
                      }
                      openLabel="Открыть просроченные договоры"
                    >
                      {data.overdueContractCount}
                    </MetricValue>
                    <span className="oup4-metric__frac-of">из {data.contractCount}</span>
                    <span
                      className={`oup4-pill${data.overdueContractCount > 0 ? ' oup4-pill--danger' : ' oup4-pill--success'}`}
                    >
                      {data.overdueContractPercent}% просрочено
                    </span>
                  </div>
                  <Meter
                    percent={data.overdueContractPercent}
                    tone={data.overdueContractCount > 0 ? 'over' : 'ok'}
                    caption="просрочено"
                    hideCaption
                  />
                </article>

                <article className="oup4-card">
                  <div className="oup4-metric__label">Задачи в просрочке</div>
                  <div className="oup4-metric__frac">
                    <MetricValue
                      tone={data.overdueTaskCount > 0 ? 'negative' : 'default'}
                      onOpen={
                        data.overdueTaskCount > 0
                          ? () =>
                              openTasks(
                                data.taskItems.filter((item) => item.overdue),
                                'Просроченные задачи',
                              )
                          : undefined
                      }
                      openLabel="Открыть просроченные задачи"
                    >
                      {data.overdueTaskCount}
                    </MetricValue>
                    <span className="oup4-metric__frac-of">из {data.taskCount}</span>
                    <span
                      className={`oup4-pill${data.overdueTaskCount > 0 ? ' oup4-pill--danger' : ' oup4-pill--success'}`}
                    >
                      {data.overdueTaskPercent}% просрочено
                    </span>
                  </div>
                  <Meter
                    percent={data.overdueTaskPercent}
                    tone={data.overdueTaskCount > 0 ? 'over' : 'ok'}
                    caption="просрочено"
                    hideCaption
                  />
                  <OverdueTasksMatrixTable
                    matrix={data.overdueTasksMatrix}
                    onCell={(row, column) =>
                      openTasks(
                        data.taskItems.filter(
                          (item) => item.row === row && item.column === column && item.overdue,
                        ),
                        `Просрочка · ${MATRIX_ROW_LABELS[row]} · ${MATRIX_COLUMN_LABELS[column]}`,
                      )
                    }
                  />
                </article>
              </div>
            </section>
          </div>
        </div>
      )}

      {drawer ? <DrillDownDrawer drawer={drawer} onClose={() => setDrawer(null)} /> : null}
    </div>
  );
};

export default DashboardOup4Page;
